import { Fragment, useEffect, useState, type ReactNode } from 'react';
import type { MeResponse } from '@kassa/shared';
import { api, ApiError } from './api';
import { Home } from './screens/Home';
import { Login, readLinkHash } from './screens/Login';
import { ProductCard } from './screens/ProductCard';
import { CustomerCard } from './screens/CustomerCard';
import { CustomerImport } from './screens/CustomerImport';
import { Customers } from './screens/Customers';
import { Import } from './screens/Import';
import { Stock } from './screens/Stock';
import { Purchases } from './screens/Purchases';
import { Shift } from './screens/Shift';
import { Sale } from './screens/Sale';
import { Transfers } from './screens/Transfers';
import { Reports } from './screens/Reports';
import { Shipments } from './screens/Shipments';
import { Approvals } from './screens/Approvals';
import { Intake } from './screens/Intake';
import { IntakeApprovals } from './screens/IntakeApprovals';
import { Return } from './screens/Return';
import { Pins } from './screens/Pins';
import { Contracts } from './screens/Contracts';
import { ContractTemplate } from './screens/ContractTemplate';
import { ChangePassword } from './screens/ChangePassword';
import { Hesabim } from './screens/Hesabim';
import { InvitationsPanel } from './components/InvitationsPanel';
import { Products } from './screens/Products';
import { SelectCompany } from './screens/SelectCompany';
import { SelectOutlet } from './screens/SelectOutlet';
import { hasPermission } from './format';
import { pendingInviteAfterGoogle, Register } from './screens/Register';
import { MobileApp } from './mobile/MobileApp';
import { isMobileApp } from './pwa';
import { AppShell, type ShellTarget } from './components/AppShell';
import { Assistant } from './screens/Assistant';
import { StockTransfers } from './screens/StockTransfers';
import { Warehouses } from './screens/Warehouses';
import { FinanceAccounts } from './screens/FinanceAccounts';
import { FinancePayment } from './screens/FinancePayment';
import { Receipts } from './screens/Receipts';
import { Scanner } from './screens/scanner/Scanner';
import { Suppliers } from './screens/Suppliers';
import { VatCheck } from './screens/VatCheck';

type View =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'login' }
  | { kind: 'register'; code: string; notice?: string }
  | { kind: 'select'; me: MeResponse }
  | { kind: 'outlet'; me: MeResponse }
  | { kind: 'home'; me: MeResponse }
  | { kind: 'nocompany'; me: MeResponse }
  | { kind: 'products'; me: MeResponse }
  | { kind: 'product'; me: MeResponse; id: string | null }
  | { kind: 'import'; me: MeResponse }
  | { kind: 'customers'; me: MeResponse }
  | { kind: 'customerImport'; me: MeResponse }
  | { kind: 'customer'; me: MeResponse; id: string | null }
  | { kind: 'stock'; me: MeResponse }
  | { kind: 'warehouses'; me: MeResponse }
  | { kind: 'stockTransfers'; me: MeResponse }
  | { kind: 'financePayment'; me: MeResponse }
  | { kind: 'financeAccounts'; me: MeResponse }
  | { kind: 'purchases'; me: MeResponse }
  | { kind: 'shift'; me: MeResponse }
  | { kind: 'sale'; me: MeResponse }
  | { kind: 'companySale'; me: MeResponse }
  | { kind: 'transfers'; me: MeResponse }
  | { kind: 'reports'; me: MeResponse }
  | { kind: 'shipments'; me: MeResponse }
  | { kind: 'approvals'; me: MeResponse }
  | { kind: 'intake'; me: MeResponse }
  | { kind: 'scanner'; me: MeResponse }
  | { kind: 'suppliers'; me: MeResponse }
  | { kind: 'vatCheck'; me: MeResponse }
  | { kind: 'intakeApprovals'; me: MeResponse }
  | { kind: 'return'; me: MeResponse }
  | { kind: 'pins'; me: MeResponse }
  | { kind: 'receipts'; me: MeResponse }
  | { kind: 'ai'; me: MeResponse; question?: string }
  | { kind: 'contracts'; me: MeResponse }
  | { kind: 'contractTemplate'; me: MeResponse; versionId: string }
  | { kind: 'soon'; me: MeResponse; label: string }
  | { kind: 'account'; me: MeResponse }
  | { kind: 'mustChange'; me: MeResponse };

// Dərin keçidlər səhifə yüklənəndə BİR DƏFƏ oxunur (modul səviyyəsində): oxumaq URL-i/sessionStorage-i təmizləyir, StrictMode
// (development) isə effekti iki dəfə işlədir — ikinci dəfə keçid itirdi (hesabim, sirket-devet brauzer testləri, KT-107).
const BOOT = {
  // Dəvət linki (#qeydiyyat=…, KT-84)
  invite: readLinkHash('qeydiyyat'),
  // Google ilə qeydiyyat alınmadısa — qeydiyyat ekranına qayıt
  backFromGoogle: pendingInviteAfterGoogle(),
  // Profildən Google bağlamadan sonra «Hesabım»a qayıt (?hesabim=1)
  account: new URLSearchParams(window.location.search).get('hesabim') === '1',
};
if (BOOT.account) window.history.replaceState(null, '', window.location.pathname);

// Cari sessiyaya görə ekran: giriş → şirkət seçimi → obyekt seçimi → ana səhifə.
function viewFor(me: MeResponse): View {
  if (me.mustChangePassword) return { kind: 'mustChange', me };
  if (me.memberships.length === 0) return { kind: 'nocompany', me };
  if (!me.tenant) return { kind: 'select', me };
  if (!me.outlet) return { kind: 'outlet', me };
  // ?hesabim=1 — yalnız ilk açılışda bir dəfə (sonrakı girişlər ana səhifəyə)
  if (BOOT.account) {
    BOOT.account = false;
    return { kind: 'account', me };
  }
  // Mobil skaner (KT-108): /skaner — telefonun ana ekranına əlavə olunan ünvan
  if (window.location.pathname.startsWith('/skaner')) return { kind: 'scanner', me };
  return { kind: 'home', me };
}

export function App() {
  const [view, setView] = useState<View>({ kind: 'loading' });
  // Mobil görünüşdən (KT-94) «Tam versiya» seçilib
  const [desktop, setDesktop] = useState(false);
  const [navSeq, setNavSeq] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Dəvət linki (#qeydiyyat=…, KT-84): qeydiyyat ekranı (girişli halda da)
    if (BOOT.invite) {
      setView({ kind: 'register', code: BOOT.invite });
      return;
    }
    // Google ilə qeydiyyat alınmadısa (səhv Google hesabı, imtina, vaxt) — kod fraqmentdə yox idi: qeydiyyat ekranına qayıt
    if (BOOT.backFromGoogle) {
      setView({ kind: 'register', code: BOOT.backFromGoogle.code, notice: BOOT.backFromGoogle.reason });
      return;
    }
    // Bərpa linki (#reset=…) girişli halda açılsa da yeni parol ekranı göstərilir (KT-75)
    if (/^#reset=/.test(window.location.hash)) {
      setView({ kind: 'login' });
      return;
    }
    // Ləğv olunmuş (StrictMode-un birinci) işin cavabı nəzərə alınmır — viewFor bir dəfə çağırılır
    api
      .me()
      .then((me) => {
        if (!cancelled) setView(me ? viewFor(me) : { kind: 'login' });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) setView({ kind: 'login' });
        else setView({ kind: 'error', message: 'Şəbəkə xətası. Səhifəni yeniləyin.' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (view.kind === 'loading') return <div className="page-center muted">Yüklənir…</div>;

  if (view.kind === 'register') {
    return <Register code={view.code} notice={view.notice} onDone={(me) => setView(viewFor(me))} onCancel={() => setView({ kind: 'login' })} />;
  }

  if (view.kind === 'error') {
    return (
      <div className="page-center">
        <p className="bad">{view.message}</p>
      </div>
    );
  }

  if (view.kind === 'login') {
    return <Login onLoggedIn={(me) => setView(viewFor(me))} />;
  }

  if (view.kind === 'nocompany') {
    return (
      <div className="page-center">
        <div className="card">
          <h2>Şirkət tapılmadı</h2>
          <p className="muted">Bu hesab heç bir şirkətdə üzv deyil. Sahibinizlə əlaqə saxlayın.</p>
          <InvitationsPanel onAccepted={(me) => setView(viewFor(me))} />
          <button type="button" className="btn-primary" onClick={() => setView({ kind: 'login' })}>
            Başqa hesabla gir
          </button>
        </div>
      </div>
    );
  }

  const logout = () => setView({ kind: 'login' });

  if (view.kind === 'select') {
    return <SelectCompany me={view.me} onSelected={(me) => setView(viewFor(me))} />;
  }

  if (view.kind === 'outlet') {
    return <SelectOutlet onSelected={(me) => setView(viewFor(me))} onLogout={logout} />;
  }

  if (view.kind === 'mustChange') {
    return <ChangePassword me={view.me} onDone={(me) => setView(viewFor(me))} onLogout={() => setView({ kind: 'login' })} />;
  }

  const me = view.me;

  // Mobil skaner (KT-108): qabıqsız tam ekran, ünvan /skaner
  if (view.kind === 'scanner') {
    if (!window.location.pathname.startsWith('/skaner')) window.history.pushState(null, '', '/skaner');
    const leave = (next: View) => {
      window.history.replaceState(null, '', '/');
      setView(next);
    };
    return <Scanner me={me} onExit={() => leave({ kind: 'home', me })} onSale={() => leave({ kind: 'sale', me })} />;
  }

  // Sahib üçün mobil tətbiq (KT-94): /m ünvanı və ya quraşdırılmış PWA — ana səhifənin yerinə
  if (view.kind === 'home' && !desktop && isMobileApp()) {
    return (
      <MobileApp
        me={me}
        onLogout={() => {
          void api.logout().catch(() => undefined).then(logout);
        }}
        onOpenAccount={() => setView({ kind: 'account', me })}
        onDesktop={() => {
          setDesktop(true);
          if (window.location.pathname.startsWith('/m')) window.history.replaceState(null, '', '/');
        }}
      />
    );
  }

  // Qabıq (KT-99): bütün iş ekranları menyu və üst zolaqla; ekranın öz başlığı bölmə rəngində başlıq olur
  // Menyudan keçid ekranı yenidən qurur (eyni bölməyə klik də): süzgəc URL ilə eyni qalsın
  const go = (to: ShellTarget) => {
    setNavSeq((n) => n + 1);
    setView(targetView(to, me));
  };
  const home = () => setView({ kind: 'home', me });
  const shell = (active: ShellTarget, node: ReactNode) => (
    <AppShell
      me={me}
      active={active}
      onNavigate={go}
      onOpen={(kind, id) => {
        setNavSeq((n) => n + 1);
        setView(kind === 'product' ? { kind: 'product', me, id } : { kind: 'customer', me, id });
      }}
      onChangeOutlet={() => setView({ kind: 'outlet', me })}
      onSwitchCompany={me.memberships.length > 1 ? () => setView({ kind: 'select', me }) : null}
      onOpenAccount={() => setView({ kind: 'account', me })}
      onLogout={logout}
    >
      <Fragment key={navSeq}>{node}</Fragment>
    </AppShell>
  );

  switch (view.kind) {
    case 'products':
      return shell(
        'products',
        <Products
          canEdit={hasPermission(me, 'product.edit')}
          canImport={hasPermission(me, 'import.excel')}
          onOpen={(id) => setView({ kind: 'product', me, id })}
          onNew={() => setView({ kind: 'product', me, id: null })}
          onImport={() => setView({ kind: 'import', me })}
        />,
      );
    case 'product':
      return shell(
        'products',
        <ProductCard
          id={view.id}
          canEdit={hasPermission(me, 'product.edit')}
          onBack={() => setView({ kind: 'products', me })}
          onSaved={(savedId) => setView({ kind: 'product', me, id: savedId })}
        />,
      );
    case 'vatCheck':
      return shell('vatCheck', <VatCheck onOpen={(id) => setView({ kind: 'product', me, id })} />);
    case 'suppliers':
      return shell('suppliers', <Suppliers canEdit={hasPermission(me, 'purchase.create')} />);
    case 'customers':
      return shell(
        'customers',
        <Customers
          canEdit={hasPermission(me, 'customer.edit')}
          onOpen={(id) => setView({ kind: 'customer', me, id })}
          onNew={() => setView({ kind: 'customer', me, id: null })}
          onImport={hasPermission(me, 'import.excel') && hasPermission(me, 'customer.edit') ? () => setView({ kind: 'customerImport', me }) : undefined}
        />,
      );
    case 'customerImport':
      return shell('customers', <CustomerImport onBack={() => setView({ kind: 'customers', me })} />);
    case 'customer':
      return shell(
        'customers',
        <CustomerCard
          id={view.id}
          canEdit={hasPermission(me, 'customer.edit')}
          canPay={hasPermission(me, 'customer.payment')}
          canAdjustBonus={hasPermission(me, 'bonus.adjust')}
          onBack={() => setView({ kind: 'customers', me })}
          onSaved={(savedId) => setView({ kind: 'customer', me, id: savedId })}
        />,
      );
    case 'import':
      return shell('import', <Import defaultOutletId={me.outlet?.id ?? null} onBack={home} />);
    case 'stock':
      return shell('stock', <Stock />);
    case 'warehouses':
      return shell(
        'warehouses',
        <Warehouses me={me} canTransfer={hasPermission(me, 'stock.transfer')} onTransfer={() => setView({ kind: 'stockTransfers', me })} />,
      );
    case 'stockTransfers':
      return shell('stockTransfers', <StockTransfers canSend={hasPermission(me, 'stock.transfer')} canReceive={hasPermission(me, 'stock.receive')} />);
    case 'financePayment':
      return shell('financePayment', <FinancePayment />);
    case 'financeAccounts':
      return shell('financeAccounts', <FinanceAccounts />);
    case 'purchases':
      return shell('purchase', <Purchases me={me} onBack={home} />);
    case 'return':
      return shell('returns', <Return me={me} onBack={home} />);
    case 'pins':
      return shell('settings', <Pins me={me} />);
    case 'account':
      return shell('home', <Hesabim me={me} onBack={home} onLogout={logout} />);
    case 'sale':
      return shell('sales', <Sale me={me} onBack={home} />);
    case 'companySale':
      return shell('company', <Sale me={me} company onBack={home} />);
    case 'intake':
      return shell('intake', <Intake onBack={home} />);
    case 'intakeApprovals':
      return shell('intakeApprovals', <IntakeApprovals onBack={home} />);
    case 'approvals':
      return shell('approvals', <Approvals onBack={home} />);
    case 'shipments':
      return shell('shipments', <Shipments onBack={home} />);
    case 'reports':
      return shell('reports', <Reports me={me} onBack={home} />);
    case 'transfers':
      return shell('transfers', <Transfers onBack={home} />);
    case 'shift':
      return shell('shift', <Shift me={me} onBack={home} />);
    case 'receipts':
      return shell('receipts', <Receipts onNewSale={hasPermission(me, 'sale.create') ? () => setView({ kind: 'sale', me }) : undefined} canCancel={hasPermission(me, 'sale.cancel')} />);
    case 'ai':
      return shell('ai', <Assistant initialQuestion={view.question} />);
    case 'contracts':
      return shell(
        'contracts',
        <Contracts onOpenTemplate={(versionId) => setView({ kind: 'contractTemplate', me, versionId })} onOpenSettings={() => setView({ kind: 'pins', me })} />,
      );
    case 'contractTemplate':
      return shell('contracts', <ContractTemplate versionId={view.versionId} onBack={() => setView({ kind: 'contracts', me })} />);
    case 'soon':
      return shell(
        'home',
        <main className="page">
          <section className="card">
            <h2>{view.label}</h2>
            <p className="muted">Bu bölmə hazırlanır.</p>
          </section>
        </main>,
      );
    default:
      return shell(
        'home',
        <Home
          me={me}
          onNavigate={go}
          onAskAi={hasPermission(me, 'ai.ask') ? (question) => setView({ kind: 'ai', me, question }) : undefined}
          onInvitationAccepted={(m) => setView(viewFor(m))}
        />,
      );
  }
}

// Menyu bölməsi → ekran
function targetView(to: ShellTarget, me: MeResponse): View {
  switch (to) {
    case 'home':
      return { kind: 'home', me };
    case 'sales':
      return { kind: 'sale', me };
    case 'receipts':
      return { kind: 'receipts', me };
    case 'company':
      return { kind: 'companySale', me };
    case 'returns':
      return { kind: 'return', me };
    case 'shift':
      return { kind: 'shift', me };
    case 'shipments':
      return { kind: 'shipments', me };
    case 'products':
      return { kind: 'products', me };
    case 'stock':
      return { kind: 'stock', me };
    case 'warehouses':
      return { kind: 'warehouses', me };
    case 'stockTransfers':
      return { kind: 'stockTransfers', me };
    case 'financePayment':
      return { kind: 'financePayment', me };
    case 'financeAccounts':
      return { kind: 'financeAccounts', me };
    case 'purchase':
      return { kind: 'purchases', me };
    case 'intake':
      return { kind: 'intake', me };
    case 'scanner':
      return { kind: 'scanner', me };
    case 'suppliers':
      return { kind: 'suppliers', me };
    case 'vatCheck':
      return { kind: 'vatCheck', me };
    case 'intakeApprovals':
      return { kind: 'intakeApprovals', me };
    case 'import':
      return { kind: 'import', me };
    case 'customers':
      return { kind: 'customers', me };
    case 'transfers':
      return { kind: 'transfers', me };
    case 'approvals':
      return { kind: 'approvals', me };
    case 'reports':
      return { kind: 'reports', me };
    case 'settings':
      return { kind: 'pins', me };
    case 'contracts':
      return { kind: 'contracts', me };
    case 'ai':
      return { kind: 'ai', me };
  }
}
